import { AnyAction } from "@reduxjs/toolkit";
import {
  SET_LANGUAGE_COMPLETE,
  RcaThunkDispatch,
  TOGGLE_NAV,
  SET_AUTOFILL_ENABLED,
  SET_COOKIE_CONSENT_REQUIRED,
  DISMISS_BANNER_COMPLETE,
  REFRESH_ANTIFORGERY_TOKEN,
  SET_THEME,
} from "./actionTypes";
import { IRcaState } from "../store/IRcaState";
import { IServices } from "../services/IServices";
import { ThemeName } from "../services/ThemeService";

/**
 * Creates an action that sets whether autocomplete is enabled in the state
 *
 * @param enabled - Whether autocomplete is enabled
 * @returns the action
 */
export const setAutoFillEnabled = (enabled: boolean): AnyAction => ({
  type: SET_AUTOFILL_ENABLED,
  enabled,
});

/**
 * Creates an action that sets the theme name in the state
 *
 * @param themeName - The theme name ("classic", "light", "dark", or "system")
 * @returns the action
 */
export const setThemeName = (themeName: ThemeName): AnyAction => ({
  type: SET_THEME,
  themeName,
});

/**
 * Creates an action that sets the language in the state
 *
 * @param language - The language that is being selected
 * @param langDir - The language direction (rtl or ltr)
 * @returns The action
 */
const setLanguageComplete = (language: string, langDir: string): AnyAction => ({
  type: SET_LANGUAGE_COMPLETE,
  language,
  langDir,
});

/**
 * Creates an action that sets the language by reloading strings and updating the store
 *
 * @param language - the new language
 * @param isRtl - a parameter indicating whether it is a right to left language
 * @returns the thunk action
 */
export const setLanguage = (language: string, isRtl: boolean) => {
  return async (dispatch: RcaThunkDispatch, _getState: () => IRcaState, services: IServices): Promise<void> => {
    await services.stringsService.loadStringsAsync(language);

    dispatch(setLanguageComplete(language, isRtl ? "rtl" : "ltr"));
  };
};

/**
 * Creates an action that sets the dismissed timestamp
 *
 * @param value - the banner ID and timestamp of dismissal
 * @returns the action
 */
const dismissBannerComplete = (value: {
  /**
   * The banner ID
   */
  bannerId: string;

  /**
   * The dismissal timestamp
   */
  timestamp: Date;
}): AnyAction => ({
  type: DISMISS_BANNER_COMPLETE,
  value,
});

/**
 * Creates an action that dismisses a banner and saves the dismissed timestamp.
 *
 * @param bannerId - The banner ID.
 * @returns the thunk action
 */
export const dismissBanner = (bannerId: string) => {
  return async (dispatch: RcaThunkDispatch, getState: () => IRcaState, services: IServices): Promise<void> => {
    const now = new Date();
    const {
      globalConfig: { antiForgeryToken },
    } = getState();
    await services.settingsService.saveSettingsAsync({ bannersDismissed: { [bannerId]: now } }, antiForgeryToken);

    dispatch(dismissBannerComplete({ bannerId, timestamp: now }));
  };
};

/**
 * Creates an action that toggles the nav
 *
 * @returns the action
 */
export const toggleNav = (): AnyAction => ({
  type: TOGGLE_NAV,
});

/**
 * Creates an action that initializes the cookie consent
 *
 * @param language - the language
 * @param elementId - the element ID for the banner
 * @returns the thunk action
 */
export const initCookieConsent = (language: string, elementId: string) => {
  return async (dispatch: RcaThunkDispatch, _getState: () => IRcaState, services: IServices): Promise<void> => {
    await services.cookieConsentService.initialize(language, elementId);

    const required = services.cookieConsentService.consentRequired();
    dispatch({
      type: SET_COOKIE_CONSENT_REQUIRED,
      required,
    });
  };
};

/**
 * Manages the current cookie consent
 *
 * @returns the thunk action
 */
export const manageCookieConsent = () => {
  return (_dispatch: RcaThunkDispatch, _getState: () => IRcaState, services: IServices): Promise<void> => {
    services.cookieConsentService.manageConsent();
    return Promise.resolve();
  };
};

/**
 * Refreshes the anti-forgery token.
 *
 * @returns the thunk action
 */
export const refreshAntiForgeryToken = () => {
  return async (dispatch: RcaThunkDispatch, _getState: () => IRcaState, services: IServices): Promise<void> => {
    const newToken = await services.antiForgeryService.getAntiForgeryToken();
    dispatch({
      type: REFRESH_ANTIFORGERY_TOKEN,
      antiForgeryToken: newToken,
    });
  };
};
